import { indexIncluded, resolveRef } from 'Douleutaras/functional-components/ServicePrices/servicePriceTree';
import { ServicePriceIncludedResource } from 'Douleutaras/functional-components/ServicePrices/types';

import {
  CatalogLevel,
  CatalogLine,
  ServicePriceItemResource,
  SettingsIncludedResource,
  SettingsServicePriceResource,
  SettingsServicePricesDocument
} from './types';

export const SERVICE_PRICE_TYPE = 'seller-service-price';
export const SERVICE_PRICE_ITEM_TYPE = 'service-price-item';

export const isPriceResource = (resource: SettingsIncludedResource): resource is SettingsServicePriceResource =>
  resource.type === SERVICE_PRICE_TYPE;

export const isItemResource = (resource: SettingsIncludedResource): resource is ServicePriceItemResource =>
  resource.type === SERVICE_PRICE_ITEM_TYPE;

export const isLabelResource = (resource: SettingsIncludedResource): resource is ServicePriceIncludedResource =>
  !isPriceResource(resource) && !isItemResource(resource);

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const toAmount = (value: string | null): number | undefined => {
  const parsed = value === null ? Number.NaN : Number(value);

  return Number.isNaN(parsed) ? undefined : parsed;
};

const toLevel = (ref?: { type: string; id: string; title: string }): CatalogLevel | undefined =>
  ref && { type: ref.type, id: ref.id, title: ref.title };

/** The catalog lines of every category, in API order. */
export const mapCatalogLines = (doc: SettingsServicePricesDocument): Array<CatalogLine> => {
  const included = doc.included ?? [];
  const itemById = new Map(included
    .filter(isItemResource)
    .map(item => [item.id, item]));
  const resourceByRef = indexIncluded(included.filter(isLabelResource));

  return doc.data.flatMap(category => (category.relationships?.items?.data ?? [])
    .map(link => itemById.get(link.id))
    .filter(isDefined)
    .map(({ id, attributes, relationships }) => ({
      itemId: id,
      categoryId: category.id,
      service: toLevel(resolveRef(resourceByRef, relationships?.sub1Service)),
      detail: toLevel(resolveRef(resourceByRef, relationships?.sub2Detail)),
      defaults: {
        priceType: attributes.catalogPriceType,
        priceUnit: attributes.catalogPriceUnit,
        amount: toAmount(attributes.catalogAmount),
        amountMax: toAmount(attributes.catalogAmountMax)
      },
      priceId: relationships?.price?.data?.id
    })));
};

const isSameLevel = (a?: CatalogLevel, b?: CatalogLevel): boolean => a?.type === b?.type && a?.id === b?.id;

/** Lines a service modal can add: the services (or the whole category) not priced yet. */
export const addableServiceLines = (lines: Array<CatalogLine>): Array<CatalogLine> =>
  lines.filter(line => !line.detail && !line.priceId);

/** Whether any line of the category has a detail, i.e. whether its services can have sub-services. */
export const hasDetailLines = (lines: Array<CatalogLine>): boolean => lines.some(line => Boolean(line.detail));

/** The detail lines under a service. */
export const detailLinesOf = (lines: Array<CatalogLine>, service?: CatalogLevel): Array<CatalogLine> =>
  service ? lines.filter(line => line.detail && isSameLevel(line.service, service)) : [];
